import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ChartDataInnerProvider, type TChartDataContext } from "./data";
import { ChartViewProvider } from "./chartview";

export { useChartApi, useChartDataContext, type TChartDataContext } from "./data";
export { useChartView, useChartViewRange } from "./chartview";

// Safety net so the overlay can't get stuck if the reset never runs (e.g. the data load errors or returns empty).
const RESET_LOADING_FALLBACK_MS = 3000;
// Longer safety net for gap recovery: a big gap fetch can legitimately run several seconds, but we never want a hung fetch to leave the overlay stuck forever.
const RECOVERING_FALLBACK_MS = 60_000;

export const ChartDataProvider = (p: { children: React.ReactNode; value: TChartDataContext }) => {
  const resetTimescaleRef = useRef(false);
  const resetTimescaleAtRef = useRef(0);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);

  const scheduleResetTimescale = useCallback(() => {
    resetTimescaleRef.current = true;
    resetTimescaleAtRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!backgroundLoading) return;
    const timeout = setTimeout(() => setBackgroundLoading(false), RESET_LOADING_FALLBACK_MS);
    return () => clearTimeout(timeout);
  }, [backgroundLoading]);

  useEffect(() => {
    if (!recovering) return;
    const timeout = setTimeout(() => setRecovering(false), RECOVERING_FALLBACK_MS);
    return () => clearTimeout(timeout);
  }, [recovering]);

  const value = useMemo<TChartDataContext>(
    () => ({ ...p.value, scheduleResetTimescale, backgroundLoading, setBackgroundLoading, recovering, setRecovering }),
    [p.value, scheduleResetTimescale, backgroundLoading, recovering],
  );

  return (
    <ChartDataInnerProvider value={value}>
      <ChartViewProvider
        id={p.value.chart?.id ?? ""}
        sampleTime={p.value.chart?.sampleTime ?? "10s"}
        resetTimescaleRef={resetTimescaleRef}
        resetTimescaleAtRef={resetTimescaleAtRef}
      >
        {p.children}
      </ChartViewProvider>
    </ChartDataInnerProvider>
  );
};
